<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useSectorPermissions } from 'dashboard/composables/useSectorPermissions';
import AddSector from './AddSector.vue';
import EditSector from './EditSector.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAccountAdmin } = useSectorPermissions();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedSector = ref({});

const records = computed(() => getters['sectors/getSectors'].value);
const uiFlags = computed(() => getters['sectors/getUIFlags'].value);
const teamsList = computed(() => getters['teams/getTeams'].value);

const sectorTeamsMap = computed(() => {
  const map = {};
  teamsList.value.forEach(team => {
    if (!team.sector_id) return;
    if (!map[team.sector_id]) map[team.sector_id] = [];
    map[team.sector_id].push(team.name);
  });
  return map;
});

const deleteMessage = computed(() => ` ${selectedSector.value.name}?`);

const tableHeaders = computed(() => [
  t('SECTOR_MGMT.LIST.TABLE_HEADER.NAME'),
  t('SECTOR_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('SECTOR_MGMT.LIST.TABLE_HEADER.TEAMS'),
]);

const openAddPopup = () => { showAddPopup.value = true; };
const hideAddPopup = () => { showAddPopup.value = false; };

const openEditPopup = sector => {
  selectedSector.value = sector;
  showEditPopup.value = true;
};
const hideEditPopup = () => { showEditPopup.value = false; };

const openDeletePopup = sector => {
  selectedSector.value = sector;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => { showDeleteConfirmationPopup.value = false; };

const deleteSector = async id => {
  try {
    await store.dispatch('sectors/delete', id);
    useAlert(t('SECTOR_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('SECTOR_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedSector.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedSector.value.id] = true;
  closeDeletePopup();
  deleteSector(selectedSector.value.id);
};

onBeforeMount(() => {
  store.dispatch('sectors/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SECTOR_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('SECTOR_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SECTOR_MGMT.HEADER')"
        :description="$t('SECTOR_MGMT.DESCRIPTION')"
        feature-name="sectors"
      >
        <template #actions>
          <Button
            v-if="isAccountAdmin"
            icon="i-lucide-circle-plus"
            :label="$t('SECTOR_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="sector in records" :key="sector.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="font-medium break-words text-n-slate-12">
                {{ sector.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ sector.description }}</td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm">
              {{ sectorTeamsMap[sector.id]?.join(', ') || '-' }}
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('SECTOR_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[sector.id]"
                  @click="openEditPopup(sector)"
                />
                <Button
                  v-if="isAccountAdmin"
                  v-tooltip.top="$t('SECTOR_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[sector.id]"
                  @click="openDeletePopup(sector)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSector @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditSector :selected-sector="selectedSector" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('SECTOR_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('SECTOR_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('SECTOR_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('SECTOR_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
